import type { V2AdminNegotiationDetailDto } from '@idc-forms/api-client'
import {
  BriefcaseBusiness,
  FileCheck2,
  FileClock,
  History,
  RotateCcw,
  UsersRound
} from 'lucide-react'
import type { ReactNode } from 'react'
import { VersionTimeline } from '@/features/entity-versions/components/version-timeline'
import { useCanReadEntityVersions } from '@/features/entity-versions/hooks/use-entity-versions'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { NAME_CONTACT_METHOD } from '@/shared/catalogs/contact-method'
import { CATALOG_CURRENCIES } from '@/shared/catalogs/currencies'
import { type EnumRiskLevel, NAME_RISK_LEVEL } from '@/shared/catalogs/risk-level'
import { NAME_UBICATION_NEGOTIATION } from '@/shared/catalogs/ubication-negotiation'
import { ScrollFade } from '@/shared/components/scroll-fade/scroll-fade'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { avatarInitial } from '@/shared/utils/avatar-initial'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { getErrorMessage, isRetryableError } from '@/shared/utils/handle-api-error'
import { useNegotiationDetailV2Query } from '../../hooks/use-negotiations-admin-v2-query'
import { CellNegotiationProperty } from '../cells/cell-negotiation-property'
import { CellNegotiationState } from '../cells/cell-negotiation-state'
import { NegotiationApprovalsTab } from './negotiation-approvals-tab'
import { NegotiationClientsTab } from './negotiation-clients-tab'
import { NegotiationDetailPanelActions } from './negotiation-detail-panel-actions'
import { NegotiationHistoryTab } from './negotiation-history-tab'

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right font-medium break-words">{value || '—'}</div>
    </div>
  )
}

function UserIdentity({
  fullname,
  avatar,
  emptyLabel
}: {
  fullname?: string | null
  avatar?: string | null
  emptyLabel: string
}) {
  if (!fullname) return <span className="text-muted-foreground">{emptyLabel}</span>

  return (
    <span className="inline-flex min-w-0 items-center justify-end gap-1.5">
      <Avatar seed={fullname} className="h-6 w-6 shrink-0">
        <AvatarImage src={avatar ?? undefined} alt={fullname} className="object-cover" />
        <AvatarFallback className="text-[9px] uppercase">
          {avatarInitial({ name: fullname })}
        </AvatarFallback>
      </Avatar>
      <span className="truncate">{fullname}</span>
    </span>
  )
}

function NegotiationSummary({ detail }: { detail: V2AdminNegotiationDetailDto }) {
  const currency = CATALOG_CURRENCIES.find((item) => item.code === detail.currency)

  return (
    <SidePanelSection>
      <SidePanelSectionBody className="gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-pretty">Resumen de la negociación</h2>
          <CellNegotiationState
            name={detail.currentState?.state?.name}
            color={detail.currentState?.state?.color}
            overdue={detail.currentState?.overdue}
            rejected={detail.currentState?.isRejected}
          />
        </div>

        <CellNegotiationProperty
          property={detail.property}
          previousProperty={detail.previousProperty}
          societyName={detail.society?.name}
        />

        <DetailRow
          label="Proyecto"
          value={
            <ProjectBadge
              name={detail.project?.name}
              code={detail.project?.code}
              className="ml-auto"
            />
          }
        />

        <DetailRow
          label="Asesor"
          value={
            <UserIdentity
              fullname={detail.advisor?.fullname}
              avatar={detail.advisor?.avatar}
              emptyLabel="Sin asesor"
            />
          }
        />
        <DetailRow label="Flujo" value={detail.workflow?.name} />
        <DetailRow
          label="Moneda"
          value={
            currency ? `${currency.symbol.default} · ${currency.other.default}` : detail.currency
          }
        />
        <DetailRow label="Inicio" value={formatHumanDateTime(detail.dates?.start)} />
        <DetailRow
          label="En estado desde"
          value={formatHumanDateTime(detail.currentState?.stateAt)}
        />
      </SidePanelSectionBody>
    </SidePanelSection>
  )
}

function NegotiationCommercialContextTab({ detail }: { detail: V2AdminNegotiationDetailDto }) {
  const contactMethod = detail.contactMethod
    ? (NAME_CONTACT_METHOD[detail.contactMethod] ?? detail.contactMethod)
    : null
  const location = detail.ubicationNegotiation
    ? (NAME_UBICATION_NEGOTIATION[detail.ubicationNegotiation] ?? detail.ubicationNegotiation)
    : null
  // `riskLevel` viaja como texto en v2 —el catálogo es de la pantalla, no del contrato—,
  // así que el diccionario se consulta con una guarda: un valor histórico que ya no está en el
  // catálogo se muestra CRUDO en vez de pintar `undefined`.
  const riskLevel = detail.riskLevel
    ? (NAME_RISK_LEVEL[detail.riskLevel as EnumRiskLevel] ?? detail.riskLevel)
    : null
  const contactLabel =
    detail.contactMethod === 'OTHER' && detail.otherContactMethod
      ? detail.otherContactMethod
      : contactMethod
  const locationLabel =
    detail.ubicationNegotiation === 'OTHER' && detail.otherUbicationNegotiation
      ? detail.otherUbicationNegotiation
      : location

  return (
    <div className="space-y-3">
      <div className="rounded-lg border p-3">
        <h3 className="mb-2 text-xs font-semibold text-muted-foreground">Organización</h3>
        <div className="space-y-2">
          <DetailRow label="Empresa" value={detail.company?.name} />
          <DetailRow label="Agencia" value={detail.agency?.name} />
        </div>
      </div>

      <div className="rounded-lg border p-3">
        <h3 className="mb-2 text-xs font-semibold text-muted-foreground">Origen comercial</h3>
        <div className="space-y-2">
          <DetailRow label="Método de contacto" value={contactLabel} />
          <DetailRow label="Ubicación" value={locationLabel} />
          <DetailRow label="Nivel de riesgo" value={riskLevel} />
        </div>
      </div>

      <div className="rounded-lg border p-3">
        <h3 className="mb-2 text-xs font-semibold text-muted-foreground">Registro</h3>
        <div className="space-y-2">
          <DetailRow label="Creada" value={formatHumanDateTime(detail.createdAt)} />
          <DetailRow
            label="Creada por"
            value={
              <UserIdentity
                fullname={detail.createdBy?.fullname}
                avatar={detail.createdBy?.avatar}
                emptyLabel="Usuario no disponible"
              />
            }
          />
          {detail.updatedAt ? (
            <DetailRow label="Última actualización" value={formatHumanDateTime(detail.updatedAt)} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function NegotiationDetailSkeleton() {
  return (
    <SidePanelSection separated={false}>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-14 w-full" />
        {Array.from({ length: 7 }).map((_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </div>
    </SidePanelSection>
  )
}

export function NegotiationDetailPanel({ negotiationId }: { negotiationId: string }) {
  const {
    data: detail,
    isLoading,
    isError,
    error,
    refetch
  } = useNegotiationDetailV2Query(negotiationId)

  const canReadVersions = useCanReadEntityVersions('negotiation')

  if (isLoading) return <NegotiationDetailSkeleton />

  if (isError || !detail) {
    /*
     * ☠️ **ACÁ SE OFRECÍA REINTENTAR UNA NEGATIVA DE PERMISOS.**
     *
     * El panel pintaba «No se pudo cargar la negociación / Verificá tu acceso o intentá
     * nuevamente» con un botón Reintentar, para CUALQUIER error. Eso lee como un fallo transitorio,
     * y para un 403 o un 404 no lo es: reintentar da exactamente lo mismo, las veces que sea.
     *
     * Se volvió visible con la negociación que uno mismo creó y no puede abrir —la fila está en la
     * tabla, el detalle contesta 403—, pero el defecto es más viejo y más ancho: es de todo 4xx.
     * `isRetryableError` ya sabía distinguirlos (sólo 5xx y red), no había que inventar nada;
     * simplemente nadie le preguntaba acá.
     *
     * El texto ahora sale de `getErrorMessage`, que traduce por `errorCode` — el contrato— y no por
     * el mensaje del servidor.
     */
    const puedeReintentar = isRetryableError(error)

    return (
      <SidePanelSection separated={false}>
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-center">
          <p className="text-sm font-medium">No se pudo abrir la negociación</p>
          <p className="mt-1 text-xs text-muted-foreground">{getErrorMessage(error)}</p>
          {puedeReintentar ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                void refetch()
              }}
            >
              <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
              Reintentar
            </Button>
          ) : null}
        </div>
      </SidePanelSection>
    )
  }

  return (
    <div className="flex flex-col">
      <NegotiationSummary detail={detail} />

      <SidePanelSection>
        <NegotiationDetailPanelActions detail={detail} />
      </SidePanelSection>

      <Tabs defaultValue="clients" className="w-full overflow-hidden">
        <SidePanelSection compact>
          <ScrollFade className="w-full" scrollClassName="overflow-y-hidden pb-1">
            <TabsList className="flex h-auto w-max min-w-full gap-1.5 rounded-none bg-transparent p-0 text-muted-foreground">
              <TabsTrigger
                value="clients"
                className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
              >
                <UsersRound aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                <span>Clientes ({detail.owners.length})</span>
              </TabsTrigger>
              <TabsTrigger
                value="context"
                className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
              >
                <BriefcaseBusiness aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                <span>Contexto</span>
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
              >
                <History aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                <span>Historial</span>
              </TabsTrigger>
              <TabsTrigger
                value="approvals"
                className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
              >
                <FileCheck2 aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                <span>Documentos</span>
              </TabsTrigger>
              {/*
                "Historial" recorre los ESTADOS del flujo; "Versiones" recorre qué DECÍA el
                expediente después de cada edición, incluidas las que nunca lo movieron de
                estado. Se ofrece sólo con `ReadEntityVersions` (ADR 0029).
              */}
              {canReadVersions ? (
                <TabsTrigger
                  value="versions"
                  className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                >
                  <FileClock aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                  <span>Versiones</span>
                </TabsTrigger>
              ) : null}
            </TabsList>
          </ScrollFade>
        </SidePanelSection>

        <SidePanelSection separated={false}>
          <TabsContent value="clients" className="mt-0">
            <NegotiationClientsTab owners={detail.owners} />
          </TabsContent>
          <TabsContent value="context" className="mt-0">
            <NegotiationCommercialContextTab detail={detail} />
          </TabsContent>
          <TabsContent value="history" className="mt-0">
            <NegotiationHistoryTab publicId={detail.id} />
          </TabsContent>
          <TabsContent value="approvals" className="mt-0">
            {/* Acá viajaba el `_id` crudo hasta que la decisión Nº 16 autorizó tocar
                `document-approval-flows`, que filtraba por la referencia (`find({ negotiation: id })`).
                Hoy el endpoint traduce el `neg_` y el panel habla un solo id. */}
            <NegotiationApprovalsTab negotiationId={detail.id} />
          </TabsContent>
          {canReadVersions ? (
            <TabsContent value="versions" className="mt-0">
              <VersionTimeline kind="negotiation" entityId={detail.id} />
            </TabsContent>
          ) : null}
        </SidePanelSection>
      </Tabs>
    </div>
  )
}
